import type { Object3D, SkinnedMesh } from "three";
import { clone as cloneSkinned } from "three/addons/utils/SkeletonUtils.js";

/** O `fetch` que os carregadores usam (os testes trocam por um falso). */
export type Fetch = (url: string, init: { signal: AbortSignal }) => Promise<Pick<Response, "ok" | "status" | "arrayBuffer">>;

export const defaultFetch: Fetch = (url, init) => globalThis.fetch(url, init);

/** O buffer é um GLB? (o fallback de SPA do Workers devolve o index.html, 200, para arquivo ausente) */
export function isGlb(buffer: ArrayBuffer): boolean {
  return buffer.byteLength >= 12 && new TextDecoder().decode(new Uint8Array(buffer, 0, 4)) === "glTF";
}

/**
 * Baixa um GLB e o entrega a `parse`, com prazo (F49 avatares, F50 criaturas): estourado, aborta o
 * download e rejeita; HTTP fora de 2xx também. O trabalho abortado não deixa rejeição solta.
 */
export async function loadWithTimeout<T>(fetch: Fetch, url: string, timeoutMs: number, parse: (buffer: ArrayBuffer) => Promise<T>): Promise<{ value: T; bytes: number }> {
  const abort = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      abort.abort();
      reject(new Error(`tempo esgotado (${timeoutMs} ms)`));
    }, timeoutMs);
  });
  const work = (async () => {
    const response = await fetch(url, { signal: abort.signal });
    if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);
    const buffer = await response.arrayBuffer();
    return { value: await parse(buffer), bytes: buffer.byteLength };
  })();
  work.catch(() => undefined);
  try {
    return await Promise.race([work, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * O molde de uma malha de um pacote com várias na mesma skin: o pacote inteiro clonado
 * (`SkeletonUtils.clone`: ossos próprios, geometria dividida), só com as malhas de `keep`. Cada
 * instância depois é um clone do molde.
 */
export function meshTemplate(root: Object3D, keep: (mesh: SkinnedMesh) => boolean): Object3D {
  const template = cloneSkinned(root);
  const drop: Object3D[] = [];
  template.traverse((o) => (o as SkinnedMesh).isSkinnedMesh && !keep(o as SkinnedMesh) && drop.push(o));
  for (const o of drop) o.removeFromParent();
  return template;
}
